import { ApiModels, MAIN_API_TOKEN, PENDO_API_TOKEN, PendoEvents } from '@local/shared';
import type { IMainApi, IPendoApi } from '@local/shared';
import type { ILog } from '@servicetitan/log-service';
import { Log } from '@servicetitan/log-service';
import { inject, injectable, symbolToken } from '@servicetitan/react-ioc';
import { action, computed, makeObservable, observable } from 'mobx';
import { SearchType } from '../types/types';

export const SEARCH_STORE_TOKEN = symbolToken<ISearchStore>('SEARCH_STORE_TOKEN');

export interface ISearchStore {
    searchInputEl?: HTMLInputElement;
    query: string;
    searchType: SearchType;
    cases: ApiModels.Case[];
    focusedResultIndex: number;
    isFocused: boolean;
    hideOnBlur: boolean;
    isChatbotSearch: boolean;
    isCaseSearch: boolean;
    isLoading: boolean;
    isError: boolean;
    isSearchResultsVisible: boolean;
    isEmptySearchResults: boolean;
    hasSearchResults: boolean;
    setSearchType(searchType: SearchType): void;
    setQuery(query: string): void;
    setCases(results: ApiModels.Case[]): void;
    clearCases(): void;
    setIsLoading(loading: boolean): void;
    setIsError(isError: boolean): void;
    setIsFocused(focused: boolean): void;
    setHideOnBlur(hideOnBlur: boolean): void;
    resetFocusedResult(): void;
    focusNextResult(): void;
    focusPreviousResult(): void;
    doSearch(query: string, abortSignal: AbortSignal): Promise<void>;
}

@injectable()
export class SearchStore implements ISearchStore {
    searchInputEl?: HTMLInputElement;

    @observable
    query: string = '';

    @observable
    searchType: SearchType = SearchType.Cases;

    @observable
    cases: ApiModels.Case[] = [];

    @observable
    isLoading: boolean = false;

    @observable
    isError: boolean = false;

    @observable
    focusedResultIndex = -1;

    @observable
    isFocused: boolean = false;

    @observable
    hideOnBlur: boolean = false;

    @computed
    get isSearchResultsVisible() {
        return this.query.length >= 3 && this.isFocused;
    }

    @computed
    get isEmptySearchResults() {
        return Boolean(this.query) && !this.isLoading && this.cases.length === 0;
    }

    @computed
    get hasSearchResults() {
        return this.cases.length > 0;
    }

    @computed
    get isChatbotSearch(): boolean {
        return this.searchType === SearchType.Chatbot;
    }

    @computed
    get isCaseSearch(): boolean {
        return this.searchType === SearchType.Cases;
    }

    constructor(
        @inject(MAIN_API_TOKEN) private readonly mainApi: IMainApi,
        @inject(PENDO_API_TOKEN) private readonly pendoApi: IPendoApi,
        @inject(Log) private readonly log: ILog
    ) {
        makeObservable(this);
    }

    @action
    resetFocusedResult() {
        this.focusedResultIndex = -1;
    }

    @action
    focusNextResult() {
        if (this.cases.length === 0) {
            return;
        }
        this.focusedResultIndex = (this.focusedResultIndex + 1) % this.cases.length;
    }

    @action
    focusPreviousResult() {
        if (this.cases.length === 0) {
            return;
        }
        if (this.focusedResultIndex === -1) {
            this.focusedResultIndex = this.cases.length - 1;
            return;
        }
        this.focusedResultIndex =
            (this.focusedResultIndex - 1 + this.cases.length) % this.cases.length;
    }

    @action
    setSearchType(searchType: SearchType) {
        this.reset();
        this.searchType = searchType;
    }

    @action
    setQuery(query: string) {
        this.query = query;
    }

    @action
    setCases(results: ApiModels.Case[]) {
        this.cases = results;
    }

    @action
    clearCases() {
        this.cases = [];
    }

    @action
    setIsLoading(loading: boolean) {
        this.isLoading = loading;
    }

    @action
    setIsError(isError: boolean) {
        this.isError = isError;
    }

    @action
    setIsFocused(focused: boolean) {
        this.isFocused = focused;
    }

    @action
    setHideOnBlur(hideOnBlur: boolean) {
        this.hideOnBlur = hideOnBlur;
    }

    @action
    async doSearch(query: string, abortSignal: AbortSignal) {
        this.setQuery(query);
        this.onBeforeSearch();

        try {
            if (query.length < 3) {
                // For queries shorter than 3 characters, clear results and do not search
                this.clearCases();
                return;
            }
            this.pendoApi.trackEvent(PendoEvents.CaseSearch);
            const cases = await this.mainApi.search(query, abortSignal);
            this.pendoApi.trackEvent(PendoEvents.CaseSearchSuccess, {
                hasResults: cases.length > 0,
            });
            this.setCases(cases);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                throw error;
            }
            const message = error instanceof Error ? error.message : 'Unknown error';
            this.log.error({
                category: 'ISH FE',
                code: 'SearchFailed',
                message,
                error,
            });
            this.pendoApi.trackEvent(PendoEvents.CaseSearchError);
            this.clearCases();
            this.setIsError(true);
        } finally {
            this.onAfterSearch();
        }
    }

    private onBeforeSearch() {
        this.setIsError(false);
        this.setIsLoading(true);
        this.resetFocusedResult();
    }

    private onAfterSearch() {
        this.setIsLoading(false);
    }

    @action
    private reset() {
        this.query = '';
        this.cases = [];
        this.isLoading = false;
        this.isError = false;
        this.focusedResultIndex = -1;
    }
}
